import { AscMarkTheWordWordsObject } from "@ascendnow/shared";
import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import AscCorrectAnswersCard from "../AscCards/AscCorrectAnswersCard/AscCorrectAnswersCard";
import AscHeading from "../AscTypography/AscHeading/AscHeading";
import styles from "./AscMarkTheWordActivity.module.scss";
import AscMarkTheWordText from "./AscMarkTheWordText/AscMarkTheWordText";

export interface AscMarkTheWordActivityProps {
  className?: string;
  loading?: boolean;
  answerLoading?: boolean;
  text: string;
  attempted?: boolean;
  attemptsCompleted?: boolean;
  disabled?: boolean;
  question: string;
  onAttempt?: (answers: string[]) => Promise<
    | {
        allCorrect: boolean;
        correctAnswers: string[];
      }
    | undefined
  >;
  response?: Pick<
    AscMarkTheWordWordsObject,
    "isMarked" | "shouldBeMarked" | "word"
  >[];
  onTouched?: () => void;
}

export default function AscMarkTheWordActivity({
  className,
  loading,
  answerLoading,
  text,
  attempted,
  attemptsCompleted,
  disabled,
  onAttempt,
  question,
  response,
  onTouched,
}: AscMarkTheWordActivityProps) {
  const [words, setWords] = useState<AscMarkTheWordWordsObject[]>([]);
  const [correct, setCorrect] = useState<boolean>();
  const isEmpty = useMemo(
    () => !words?.some((word) => word?.isMarked),
    [words]
  );
  const [showCorrectAnswers, setShowCorrectAnswers] = useState(false);

  function handleTryAgain() {
    setShowCorrectAnswers(false);
    setCorrect(undefined);
    setWords(
      words?.map((word) => ({
        ...word,
        isMarked: false,
        shouldBeMarked: false,
      }))
    );
  }

  async function handleAttempt() {
    const correctAnswers = await onAttempt?.(
      words
        ?.filter(
          (word, index) =>
            word?.isMarked &&
            words?.findIndex((w) => w?.value === word?.value) === index
        )
        ?.map((word) => word?.word)
    );
    setShowCorrectAnswers(true);
    setCorrect(correctAnswers?.allCorrect);
    setWords(
      words?.map((word) => ({
        ...word,
        shouldBeMarked: correctAnswers?.correctAnswers?.includes(word?.value),
      }))
    );
  }

  function handleOnChange(words: AscMarkTheWordWordsObject[]) {
    setWords(words);
    if (words?.some((w) => w?.isMarked)) {
      onTouched?.();
    }
  }

  useEffect(() => {
    if (!attemptsCompleted || !response?.length) {
      return;
    }

    setWords((prev) =>
      prev?.map((word) => ({
        ...word,
        ...(response?.find((w) => w?.word === word?.word) || {}),
      }))
    );
    setCorrect(
      response?.every((word) => word?.isMarked === word?.shouldBeMarked)
    );
    setShowCorrectAnswers(true);
  }, [attemptsCompleted, response]);

  return (
    <div
      data-testid="asc-mark-the-word-activity"
      className={classNames(styles.ground, className)}
    >
      <AscHeading loading={loading} level={3} className={styles.question}>
        {question}
      </AscHeading>
      <AscMarkTheWordText
        text={text}
        disabled={disabled || attemptsCompleted || showCorrectAnswers}
        words={words}
        onChange={handleOnChange}
        loading={loading || answerLoading}
        showCorrectAnswers={showCorrectAnswers}
        attemptsCompleted={attemptsCompleted}
      />
      <AscCorrectAnswersCard
        type="secondary"
        className={classNames(styles.correctAnswer)}
        loading={loading || answerLoading}
        attempted={attempted}
        attemptsCompleted={attemptsCompleted}
        disabled={disabled || isEmpty}
        correct={correct}
        checkAnswerText="Submit"
        tryAgainText="Try again"
        onAttempt={handleAttempt}
        onTryAgain={handleTryAgain}
        showCorrectAnswers={showCorrectAnswers}
      />
    </div>
  );
}
